import {Component, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from "@angular/forms";
import {dayjsToNgbDate, ngbDateToDayjs} from "../../shared/utils/utils";
import dayjs from "dayjs";
import {IAirport} from "../../shared/models/airport.model";
import {AirportsService} from "../../services/airports.service";
import {UserService} from "../../services/user.service";
import {debounceTime, forkJoin, map, Observable, Subject, tap} from "rxjs";
import {UserLegAssignmentService} from "../../services/user-leg-assignment.service";
import {IUser} from "../../shared/models/user.model";
import {IUserLegAssignmentModel} from "../../shared/models/user-leg-assignment.model";
import {IGenericContainerObject} from "../../shared/models/genericContainerObject.model";
import {LegsService} from "../../services/legs.service";
import {ILegsModel} from "../../shared/models/legs.model";
import {takeUntil} from "rxjs/operators";
import {AuthService} from "../../services/auth.service";
import {ToastService} from "../../services/toast.service";
import {PermissionService} from "../../services/permission.service";
import {Access, PermissionUIMasks} from "../../shared/constants/enums";
import {IPairsModel} from "../../shared/models/pairs.model";
import {StaticUserGroupConstants} from "../../shared/constants/static-user-group.constants";

@Component({
  selector: 'app-user-assignment',
  templateUrl: './user-assignment.component.html',
  styleUrls: ['./user-assignment.component.scss']
})
export class UserAssignmentComponent implements OnInit, OnDestroy {

  form: UntypedFormGroup;
  stations: IAirport[];
  users: IUser[];
  usersKV: IGenericContainerObject<IUser> = {};
  legAssignments: IUserLegAssignmentModel[];
  legAssignmentsKV: IGenericContainerObject<IUserLegAssignmentModel> = {};
  selectedUserId: number;
  legs: ILegsModel[];
  unsubscribe$ = new Subject();
  selectedLegIds: number[] = [];
  deleting: number[] = [];
  pageSize = 10;
  currentPage = 0;
  isBusy = false;
  assignmentsPerUser: IGenericContainerObject<number> = {};
  filteredFlights: ILegsModel[] = [];
  pageAccess: Access;
  pairs: IPairsModel[];
  hasPermission: boolean;
  canAssign: boolean;

  constructor(private airportService: AirportsService, private userService: UserService, private userLegAssignmentService: UserLegAssignmentService, private legService: LegsService, private authService: AuthService, private toastService: ToastService, private permissionService: PermissionService) {
    this.permissionService.getPermissionAccess(PermissionUIMasks.WEB_RAMP_AGENT_ASSIGNMENT).then((pageAccess) => {
      this.pageAccess = pageAccess;
      if (!this.pageAccess) {
        return;
      }
      this.airportService.fetchAirports().subscribe((res) => {
        this.stations = res;
      });
      forkJoin([this.userService.fetchUsers({
        isActive: true,
        userGroup: StaticUserGroupConstants.STR_TO_ID.RAMP_AGENT,
        location: this.authService?.user?.userGroup === StaticUserGroupConstants.STR_TO_ID.DUTY_MANAGER ? this.authService?.user?.location : undefined,
      }), this.fetchUserLegAssignments(), this.fetchFlights()]).subscribe(([users, _, legs]) => {
        this.users = users;
        this.selectedUserId = window.history.state?.userId;
        users.forEach((user) => {
          this.usersKV[user.id] = user;
        });
        this.legs = legs;
        this.filterFlights();
      });
    });

  }

  fetchUserLegAssignments() {
    return this.userLegAssignmentService.getUserLegAssignment({isActive: true}).pipe(tap((res) => {
      this.legAssignments = res;
      this.legAssignmentsKV = {};
      this.legAssignments.forEach((assignment) => {
        this.legAssignmentsKV[assignment.arrivalLegId] = assignment;
      });
      this.assignmentsPerUser = {};
      this.legAssignments.forEach((assignment) => {
        if (!this.assignmentsPerUser[assignment.userId]) {
          this.assignmentsPerUser[assignment.userId] = 0;
        }
        this.assignmentsPerUser[assignment.userId]++;
      });
    }));
  }

  ngOnInit(): void {
    this.form = new UntypedFormGroup({
      date: new UntypedFormControl(dayjsToNgbDate(dayjs.utc())),
      flightNr: new UntypedFormControl(),
      station: new UntypedFormControl({
        value: this.authService.user?.userGroup === StaticUserGroupConstants.STR_TO_ID.DUTY_MANAGER ? this.authService.user.location : null,
        disabled: this.authService.user?.userGroup === StaticUserGroupConstants.STR_TO_ID.DUTY_MANAGER
      }),
      status: new UntypedFormControl(),
    });
    for (const key of ['date', 'flightNr', 'station', 'status']) {
      this.addListener(key);
    }
  }

  addListener(name: string) {
    this.form.get(name).valueChanges.pipe(takeUntil(this.unsubscribe$), debounceTime(250)).subscribe(() => {
      if (name === 'status') {
        this.filterFlights();
        return;
      }
      this.fetchFlights().subscribe((res) => {
        this.legs = res;
        this.filterFlights();
      });
    });
  }

  ngOnDestroy() {
    this.unsubscribe$.next(undefined);
    this.unsubscribe$.complete();
  }

  filterFlights() {
    this.currentPage = 0;
    this.filteredFlights = this.legs.filter((leg) => {
      if (this.selectedUserId) {
        if (leg.arrivalStation !== this.usersKV[this.selectedUserId]?.location) {
          return false;
        }
      }
      const val = this.form.value;
      if (val.status === true && !this.legAssignmentsKV[leg.id]) {
        return false;
      }
      return !(val.status === false && this.legAssignmentsKV[leg.id]);
    });
  }

  async changedUser(id: number, event: Event) {
    const elem = event.target as HTMLInputElement;
    this.selectedUserId = elem.checked ? id : null;
    if (!this.selectedUserId) {
      this.selectedLegIds.splice(0);
    }
    this.filterFlights();
  }

  fetchFlights() {
    const val = this.form?.value;
    this.selectedLegIds?.splice(0);
    return this.legService.getLegs({
      tod: val?.date ? {
        from: ngbDateToDayjs(this.form.value.date).toDate(),
        to: ngbDateToDayjs(this.form.value.date).endOf('day').toDate()
      } : val === undefined ? {
        from: dayjs.utc().startOf('day').toDate(),
        to: dayjs.utc().endOf('day').toDate()
      } : undefined,
      flightNumber: val?.flightNr ? val?.flightNr : undefined,
      arrivalStation: this.authService.user?.userGroup === StaticUserGroupConstants.STR_TO_ID.DUTY_MANAGER ? this.authService.user.location : val?.station || undefined,
      isActive: true,
    }).pipe(map((data: ILegsModel[]) => {
      return data.sort((a: ILegsModel, b: ILegsModel) => a.sta > b.sta ? 1 : b.sta > a.sta ? -1 : 0);
    }));
  }

  trackByFunction(index: number, item: ILegsModel) {
    return item.id; // or whatever unique identifier you wish to use
  }

  flightClicked(id: number) {
    if (!this.selectedUserId) {
      return;
    }
    const index = this.selectedLegIds?.findIndex((legId) => legId === id);
    if (index !== -1) {
      this.selectedLegIds?.splice(index, 1);
      return;
    }
    this.selectedLegIds.push(id);
  }

  async unassignAgent(userLegAssignmentId: number) {
    if (this.pageAccess !== Access.RW) {
      return;
    }
    const assignment = this.legAssignments.find((ass) => ass.id === userLegAssignmentId);
    const station = this.legs.find((leg) => leg.id === assignment.arrivalLegId).arrivalStationId;
    if (await this.permissionService.getPermissionAccess(PermissionUIMasks.WEB_RAMP_AGENT_ASSIGNMENT, station) !== Access.RW) {
      return;
    }
    this.deleting.push(userLegAssignmentId);
    this.userLegAssignmentService.deleteUserLegAssignment(userLegAssignmentId).subscribe((res) => {
      const indexId = this.deleting.findIndex((id) => id === userLegAssignmentId);
      if (res) {
        this.fetchUserLegAssignments().subscribe(() => {
          if (indexId !== -1) {
            this.deleting.splice(indexId, 1);
          }
        });
        return;
      }
      if (indexId !== -1) {
        this.deleting.splice(indexId, 1);
      }

    });
  }

  public readonly Math = Math;

  assignFlights() {
    if (!this.selectedUserId || !this.selectedLegIds?.length || this.pageAccess !== Access.RW) {
      return;
    }
    const obs: Observable<IUserLegAssignmentModel>[] = [];
    for (const selectedLegId of this.selectedLegIds) {
      obs.push(this.userLegAssignmentService.saveUserLegAssignment({
        userId: this.selectedUserId,
        arrivalLegId: selectedLegId,
        isActive: true,
      }));
    }
    if (obs.length) {
      this.isBusy = true;
      forkJoin(obs).subscribe((res) => {
        this.toastService.showSuccess('Ramp agent has been assigned');
        this.fetchUserLegAssignments().subscribe(() => {
          this.isBusy = false;
        });
      });
    }
  }

  public readonly Access = Access;
}
